/**
 * Home hero — "pin & fill on scroll".
 *
 * The sticky .home-hero__viewport keeps the hero on screen while the media
 * frame expands from an inset banner to full-bleed in place; once filled, the
 * pin releases and the page scrolls on. The outer .home-hero is grown into a
 * scroll track whose extra height (beyond the viewport) is the fill distance —
 * set here in JS (not measured from CSS) so it is always deterministic. Fill
 * progress is written to `--home-hero-fill` (0 → 1) directly on scroll (no
 * transition — see home-hero.scss); all visual interpolation lives in the SCSS.
 */

// Fraction of a viewport of scrolling over which the fill completes.
const FILL_RATIO = 0.9;

// Toggled on the masthead once the hero has fully filled (see masthead.scss).
const MASTHEAD_RELEASED_CLASS = 'is-hero-released';

function setupHero(hero) {
	const viewport = hero.querySelector('[data-home-hero-viewport]');
	if (!viewport) return;

	// The masthead floats over the hero and stays pinned (sticky, in masthead.scss)
	// while the media fills. Once the fill completes we release it so it scrolls
	// away with the hero instead of staying stuck over the content below.
	const masthead = document.querySelector('.masthead');

	let ticking = false;
	let fillDistance = 1;

	const setHeight = () => {
		// Extra scroll room beyond the hero's own (resting) height. The sticky
		// viewport pins for exactly this distance; 1px scroll = 1px of fill.
		fillDistance = Math.max(1, Math.round(window.innerHeight * FILL_RATIO));
		hero.style.height = `${viewport.offsetHeight + fillDistance}px`;
	};

	const update = () => {
		ticking = false;
		const rect = hero.getBoundingClientRect();
		const scrolledPast = Math.max(0, -rect.top);
		const progress = Math.min(1, scrolledPast / fillDistance);
		hero.style.setProperty('--home-hero-fill', progress.toFixed(4));

		if (masthead) {
			const filled = progress >= 1;
			if (filled) {
				// Freeze the masthead at the document position it reached when the
				// fill completed, so releasing it (sticky → absolute) doesn't jump.
				const releaseTop = Math.round(rect.top + window.scrollY + fillDistance);
				masthead.style.setProperty('--masthead-release-top', `${releaseTop}px`);
			}
			masthead.classList.toggle(MASTHEAD_RELEASED_CLASS, filled);
		}
	};

	const onScroll = () => {
		if (!ticking) {
			ticking = true;
			requestAnimationFrame(update);
		}
	};

	const onResize = () => {
		setHeight();
		update();
	};

	setHeight();
	update();
	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', onResize, { passive: true });
}

function init() {
	const heroes = document.querySelectorAll('.home-hero');
	if (!heroes.length) return;

	// Respect reduced-motion: show the media full-bleed at the hero's natural
	// height (no pinning / scroll track).
	if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		heroes.forEach((hero) => hero.style.setProperty('--home-hero-fill', '1'));
		return;
	}

	heroes.forEach(setupHero);
}

if (document.readyState !== 'loading') {
	init();
} else {
	document.addEventListener('DOMContentLoaded', init);
}
